/** Shared fictional demo data — coherent across all routes. Development only. */

export type Side = 'long' | 'short'
export type Session = 'Asia' | 'London' | 'New York' | 'Overlap'
export type AccountStatus = 'verified' | 'pending' | 'read-only'

export interface EquityPoint {
  date: string
  equity: number
  balance: number
}

export interface MonthlyReturn {
  year: number
  month: number // 1-12
  ret: number // percent
}

export interface Trade {
  id: string
  openTime: string
  closeTime: string
  symbol: string
  side: Side
  lots: number
  entry: number
  exit: number
  pnl: number
  pips: number
  rMultiple: number
  setup: string
  tags: string[]
  session: Session
  weekday: string
  notes: string
  mistakes: string[]
  ruleBreaks: string[]
  screenshot?: boolean
}

export interface Account {
  id: string
  name: string
  broker: string
  platform: string
  currency: string
  leverage: string
  status: AccountStatus
  verifiedSince: string
  trackRecordDays: number
  freshnessMinutes: number
  startingBalance: number
  balance: number
  equity: number
  netDeposit: number
  deposits: number
  withdrawals: number
  absoluteReturn: number
  netReturnPct: number
  maxDrawdownPct: number
  currentDrawdownPct: number
  sharpe: number
  sortino: number
  profitFactor: number
  winRate: number
  expectancy: number
  avgWin: number
  avgLoss: number
  recoveryFactor: number
  trades: number
  strategyAllocation: { name: string; pct: number; color: string }[]
  symbolExposure: { symbol: string; pct: number; pnl: number }[]
  currencyExposure: { ccy: string; pct: number }[]
  longShort: { side: string; pnl: number; winRate: number; trades: number }[]
  sessionPerf: { session: Session; pnl: number; trades: number }[]
  weekdayPerf: { day: string; pnl: number }[]
  openRisk: {
    positions: number
    notional: number
    marginUsedPct: number
    largestSymbol: string
    heat: number
  }
  equityCurve: EquityPoint[]
  monthly: MonthlyReturn[]
  verificationTimeline: { date: string; event: string; detail: string }[]
}

export interface JournalStats {
  ruleAdherence: number
  revengeScore: number
  overtradingScore: number
  bestSetup: string
  worstMistake: string
  whatChanged: string[]
}

const START = new Date('2025-01-06T00:00:00Z')

function seeded(n: number) {
  const x = Math.sin(n * 12.9898) * 43758.5453
  return x - Math.floor(x)
}

function buildEquity(days: number, startBal: number): EquityPoint[] {
  const pts: EquityPoint[] = []
  let equity = startBal
  let balance = startBal
  for (let i = 0; i < days; i++) {
    const d = new Date(START)
    d.setUTCDate(START.getUTCDate() + i)
    const r = (seeded(i + 3) - 0.46) * 0.012 + (seeded(i * 1.7) - 0.5) * 0.004
    // mild trend + mean reversion
    const drift = 0.00055
    equity = Math.max(startBal * 0.82, equity * (1 + drift + r))
    if (i % 17 === 0 && i > 0) balance = equity // transfer floating
    else if (i === 90) {
      balance += 5000
      equity += 5000
    } else if (i === 200) {
      balance -= 2500
      equity -= 2500
    } else {
      balance = balance * 0.15 + equity * 0.85
    }
    pts.push({
      date: d.toISOString().slice(0, 10),
      equity: Math.round(equity * 100) / 100,
      balance: Math.round(balance * 100) / 100,
    })
  }
  return pts
}

function buildMonthly(curve: EquityPoint[]): MonthlyReturn[] {
  const byKey = new Map<string, { first: number; last: number; y: number; m: number }>()
  for (const p of curve) {
    const [ys, ms] = p.date.split('-')
    const y = Number(ys)
    const m = Number(ms)
    const key = `${y}-${m}`
    const cur = byKey.get(key)
    if (!cur) byKey.set(key, { first: p.equity, last: p.equity, y, m })
    else cur.last = p.equity
  }
  return [...byKey.values()].map((v) => ({
    year: v.y,
    month: v.m,
    ret: Math.round(((v.last / v.first - 1) * 100) * 10) / 10,
  }))
}

const SETUPS = [
  'London sweep reclaim',
  'NY continuation',
  'Asia range fade',
  'News fade delayed',
  'HTF supply tap',
  'Break and retest',
]

const SYMBOLS = ['EURUSD', 'GBPUSD', 'XAUUSD', 'USDJPY', 'NAS100', 'US30', 'AUDUSD', 'BTCUSD']

function buildTrades(count: number): Trade[] {
  const trades: Trade[] = []
  const days = 400
  for (let i = 0; i < count; i++) {
    const dayOffset = Math.floor(seeded(i * 9.1) * days)
    const open = new Date(START)
    open.setUTCDate(START.getUTCDate() + dayOffset)
    open.setUTCHours(Math.floor(seeded(i * 2.2) * 23), Math.floor(seeded(i * 3.3) * 60), 0, 0)
    const holdH = 1 + Math.floor(seeded(i * 4.4) * 36)
    const close = new Date(open.getTime() + holdH * 3600_000)
    const win = seeded(i * 5.5) > 0.42
    const mag = 40 + seeded(i * 6.6) * 420
    const pnl = Math.round((win ? mag : -mag * (0.55 + seeded(i) * 0.5)) * 100) / 100
    const side: Side = seeded(i * 7.7) > 0.48 ? 'long' : 'short'
    const symbol = SYMBOLS[Math.floor(seeded(i * 8.8) * SYMBOLS.length)]!
    const setup = SETUPS[Math.floor(seeded(i * 1.1) * SETUPS.length)]!
    const sessionRoll = seeded(i * 1.3)
    const session: Session =
      sessionRoll < 0.22 ? 'Asia' : sessionRoll < 0.5 ? 'London' : sessionRoll < 0.78 ? 'New York' : 'Overlap'
    const mistakes: string[] = []
    const ruleBreaks: string[] = []
    const tags: string[] = [setup.split(' ')[0]!.toLowerCase()]
    if (!win && seeded(i * 2.9) > 0.65) mistakes.push('Moved stop')
    if (!win && seeded(i * 3.1) > 0.75) mistakes.push('Chased entry')
    if (seeded(i * 3.4) > 0.88) {
      mistakes.push('Revenge sequence')
      tags.push('revenge')
    }
    if (seeded(i * 3.7) > 0.9) {
      ruleBreaks.push('Size > plan')
      tags.push('oversize')
    }
    if (seeded(i * 4.1) > 0.85) tags.push('A+')
    const weekdays = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
    trades.push({
      id: `T-${1000 + i}`,
      openTime: open.toISOString(),
      closeTime: close.toISOString(),
      symbol,
      side,
      lots: Math.round((0.1 + seeded(i * 4.8) * 1.8) * 100) / 100,
      entry: Math.round((1 + seeded(i * 5.2) * 2000) * 100) / 100,
      exit: Math.round((1 + seeded(i * 5.9) * 2000) * 100) / 100,
      pnl,
      pips: Math.round((pnl / 12) * 10) / 10,
      rMultiple: Math.round((pnl / 120) * 100) / 100,
      setup,
      tags,
      session,
      weekday: weekdays[open.getUTCDay()]!,
      notes:
        win
          ? 'Plan matched. Waited for confirmation candle. Partial at 1R.'
          : 'Thesis invalid sooner than expected. Review session filter.',
      mistakes,
      ruleBreaks,
      screenshot: seeded(i * 6.2) > 0.7,
    })
  }
  return trades.sort((a, b) => (a.closeTime < b.closeTime ? 1 : -1))
}

const equityGrok = buildEquity(420, 25000)
const equityDesk = buildEquity(380, 100000)
const equityProp = buildEquity(160, 50000)

export const TRADES = buildTrades(86)

export const ACCOUNTS: Account[] = [
  {
    id: 'grok-alpha',
    name: 'Grok Alpha DEMO',
    broker: 'Demo Broker (fictional)',
    platform: 'MetaTrader 5',
    currency: 'USD',
    leverage: '1:100',
    status: 'verified',
    verifiedSince: '2025-01-08',
    trackRecordDays: 420,
    freshnessMinutes: 4,
    startingBalance: 25000,
    balance: equityGrok.at(-1)!.balance,
    equity: equityGrok.at(-1)!.equity,
    netDeposit: 22500,
    deposits: 30000,
    withdrawals: 7500,
    absoluteReturn: Math.round(equityGrok.at(-1)!.equity - 22500),
    netReturnPct: Math.round(((equityGrok.at(-1)!.equity / 22500 - 1) * 100) * 10) / 10,
    maxDrawdownPct: 11.4,
    currentDrawdownPct: 2.1,
    sharpe: 1.42,
    sortino: 2.08,
    profitFactor: 1.67,
    winRate: 54.2,
    expectancy: 38.4,
    avgWin: 186.2,
    avgLoss: -112.5,
    recoveryFactor: 3.1,
    trades: TRADES.length,
    strategyAllocation: [
      { name: 'Sweep reclaim', pct: 34, color: '#b8ff3c' },
      { name: 'Continuation', pct: 28, color: '#5ec8ff' },
      { name: 'Mean reversion', pct: 22, color: '#ffb020' },
      { name: 'Event delay', pct: 16, color: '#c084fc' },
    ],
    symbolExposure: [
      { symbol: 'XAUUSD', pct: 28, pnl: 4120 },
      { symbol: 'EURUSD', pct: 22, pnl: 1890 },
      { symbol: 'NAS100', pct: 18, pnl: 2460 },
      { symbol: 'GBPUSD', pct: 14, pnl: 920 },
      { symbol: 'USDJPY', pct: 10, pnl: -340 },
      { symbol: 'Other', pct: 8, pnl: 410 },
    ],
    currencyExposure: [
      { ccy: 'USD', pct: 42 },
      { ccy: 'EUR', pct: 18 },
      { ccy: 'XAU', pct: 16 },
      { ccy: 'GBP', pct: 12 },
      { ccy: 'JPY', pct: 8 },
      { ccy: 'Other', pct: 4 },
    ],
    longShort: [
      { side: 'Long', pnl: 6840, winRate: 56.1, trades: 48 },
      { side: 'Short', pnl: 2620, winRate: 51.8, trades: 38 },
    ],
    sessionPerf: [
      { session: 'London', pnl: 4120, trades: 31 },
      { session: 'New York', pnl: 2980, trades: 27 },
      { session: 'Overlap', pnl: 1860, trades: 16 },
      { session: 'Asia', pnl: 500, trades: 12 },
    ],
    weekdayPerf: [
      { day: 'Mon', pnl: 820 },
      { day: 'Tue', pnl: 1940 },
      { day: 'Wed', pnl: 2210 },
      { day: 'Thu', pnl: 1680 },
      { day: 'Fri', pnl: 810 },
    ],
    openRisk: {
      positions: 2,
      notional: 84200,
      marginUsedPct: 7.4,
      largestSymbol: 'XAUUSD',
      heat: 0.28,
    },
    equityCurve: equityGrok,
    monthly: buildMonthly(equityGrok),
    verificationTimeline: [
      {
        date: '2025-01-08',
        event: 'Read-only link established',
        detail: 'Reporter EA heartbeat accepted. Investor password never collected.',
      },
      {
        date: '2025-01-09',
        event: 'Baseline snapshot sealed',
        detail: 'Starting balance and open positions checksum stored.',
      },
      {
        date: '2025-04-12',
        event: 'Deposit reconciled',
        detail: '+$5,000 external deposit matched to balance jump.',
      },
      {
        date: '2025-07-30',
        event: 'Withdrawal reconciled',
        detail: '−$2,500 withdrawal excluded from pure performance.',
      },
      {
        date: '2026-07-25',
        event: 'Continuous verification',
        detail: 'Hash chain intact. Last trade digest matched.',
      },
    ],
  },
  {
    id: 'desk-book',
    name: 'Desk Book DEMO',
    broker: 'Prime Demo (fictional)',
    platform: 'MetaTrader 5',
    currency: 'USD',
    leverage: '1:50',
    status: 'verified',
    verifiedSince: '2025-02-01',
    trackRecordDays: 380,
    freshnessMinutes: 11,
    startingBalance: 100000,
    balance: equityDesk.at(-1)!.balance,
    equity: equityDesk.at(-1)!.equity,
    netDeposit: 100000,
    deposits: 100000,
    withdrawals: 0,
    absoluteReturn: Math.round(equityDesk.at(-1)!.equity - 100000),
    netReturnPct: Math.round(((equityDesk.at(-1)!.equity / 100000 - 1) * 100) * 10) / 10,
    maxDrawdownPct: 8.6,
    currentDrawdownPct: 1.2,
    sharpe: 1.18,
    sortino: 1.74,
    profitFactor: 1.41,
    winRate: 51.0,
    expectancy: 62.0,
    avgWin: 410,
    avgLoss: -290,
    recoveryFactor: 2.4,
    trades: 210,
    strategyAllocation: [
      { name: 'Index trend', pct: 40, color: '#b8ff3c' },
      { name: 'FX carry sleeve', pct: 25, color: '#5ec8ff' },
      { name: 'Metals swing', pct: 20, color: '#ffb020' },
      { name: 'Hedge overlay', pct: 15, color: '#ff4d4d' },
    ],
    symbolExposure: [
      { symbol: 'NAS100', pct: 30, pnl: 8200 },
      { symbol: 'XAUUSD', pct: 22, pnl: 4100 },
      { symbol: 'EURUSD', pct: 18, pnl: 1900 },
      { symbol: 'US30', pct: 15, pnl: 2600 },
      { symbol: 'USDJPY', pct: 15, pnl: -800 },
    ],
    currencyExposure: [
      { ccy: 'USD', pct: 55 },
      { ccy: 'XAU', pct: 15 },
      { ccy: 'EUR', pct: 12 },
      { ccy: 'JPY', pct: 10 },
      { ccy: 'Other', pct: 8 },
    ],
    longShort: [
      { side: 'Long', pnl: 12400, winRate: 52.4, trades: 120 },
      { side: 'Short', pnl: 3600, winRate: 48.9, trades: 90 },
    ],
    sessionPerf: [
      { session: 'New York', pnl: 7200, trades: 88 },
      { session: 'London', pnl: 5400, trades: 70 },
      { session: 'Overlap', pnl: 2800, trades: 32 },
      { session: 'Asia', pnl: 600, trades: 20 },
    ],
    weekdayPerf: [
      { day: 'Mon', pnl: 2100 },
      { day: 'Tue', pnl: 3800 },
      { day: 'Wed', pnl: 4100 },
      { day: 'Thu', pnl: 3500 },
      { day: 'Fri', pnl: 2500 },
    ],
    openRisk: {
      positions: 5,
      notional: 420000,
      marginUsedPct: 12.1,
      largestSymbol: 'NAS100',
      heat: 0.41,
    },
    equityCurve: equityDesk,
    monthly: buildMonthly(equityDesk),
    verificationTimeline: [
      {
        date: '2025-02-01',
        event: 'Desk portfolio enrolled',
        detail: 'Multi-strategy book under read-only verification.',
      },
      {
        date: '2026-07-25',
        event: 'Freshness check',
        detail: 'All sleeves reporting within SLA.',
      },
    ],
  },
  {
    id: 'prop-stage',
    name: 'Prop Stage DEMO',
    broker: 'Eval Sim (fictional)',
    platform: 'MetaTrader 5',
    currency: 'USD',
    leverage: '1:30',
    status: 'read-only',
    verifiedSince: '2026-02-14',
    trackRecordDays: 160,
    freshnessMinutes: 2,
    startingBalance: 50000,
    balance: equityProp.at(-1)!.balance,
    equity: equityProp.at(-1)!.equity,
    netDeposit: 50000,
    deposits: 50000,
    withdrawals: 0,
    absoluteReturn: Math.round(equityProp.at(-1)!.equity - 50000),
    netReturnPct: Math.round(((equityProp.at(-1)!.equity / 50000 - 1) * 100) * 10) / 10,
    maxDrawdownPct: 4.8,
    currentDrawdownPct: 0.6,
    sharpe: 1.91,
    sortino: 2.55,
    profitFactor: 1.88,
    winRate: 57.4,
    expectancy: 44.2,
    avgWin: 210,
    avgLoss: -130,
    recoveryFactor: 4.0,
    trades: 74,
    strategyAllocation: [
      { name: 'Intraday A+', pct: 55, color: '#b8ff3c' },
      { name: 'Session open', pct: 30, color: '#5ec8ff' },
      { name: 'Scalp filter', pct: 15, color: '#ffb020' },
    ],
    symbolExposure: [
      { symbol: 'EURUSD', pct: 35, pnl: 2100 },
      { symbol: 'GBPUSD', pct: 25, pnl: 980 },
      { symbol: 'XAUUSD', pct: 25, pnl: 1540 },
      { symbol: 'NAS100', pct: 15, pnl: 420 },
    ],
    currencyExposure: [
      { ccy: 'USD', pct: 40 },
      { ccy: 'EUR', pct: 25 },
      { ccy: 'GBP', pct: 18 },
      { ccy: 'XAU', pct: 17 },
    ],
    longShort: [
      { side: 'Long', pnl: 3200, winRate: 58.0, trades: 40 },
      { side: 'Short', pnl: 1840, winRate: 56.5, trades: 34 },
    ],
    sessionPerf: [
      { session: 'London', pnl: 2480, trades: 30 },
      { session: 'Overlap', pnl: 1620, trades: 22 },
      { session: 'New York', pnl: 840, trades: 16 },
      { session: 'Asia', pnl: 100, trades: 6 },
    ],
    weekdayPerf: [
      { day: 'Mon', pnl: 640 },
      { day: 'Tue', pnl: 1120 },
      { day: 'Wed', pnl: 1380 },
      { day: 'Thu', pnl: 1210 },
      { day: 'Fri', pnl: 690 },
    ],
    openRisk: {
      positions: 1,
      notional: 28000,
      marginUsedPct: 3.2,
      largestSymbol: 'EURUSD',
      heat: 0.12,
    },
    equityCurve: equityProp,
    monthly: buildMonthly(equityProp),
    verificationTimeline: [
      {
        date: '2026-02-14',
        event: 'Prop ruleset attached',
        detail: 'Daily loss and max DD budgets mirrored from challenge preset.',
      },
      {
        date: '2026-07-25',
        event: 'No rule breach window',
        detail: '42 consecutive sessions inside limits.',
      },
    ],
  },
]

export const PRIMARY = ACCOUNTS[0]!

export const JOURNAL_STATS: JournalStats = {
  ruleAdherence: 86,
  revengeScore: 18,
  overtradingScore: 24,
  bestSetup: 'London sweep reclaim',
  worstMistake: 'Moved stop',
  whatChanged: [
    'Cut Asia range fades after three consecutive −R weeks.',
    'Raised minimum A+ checklist score from 3/5 to 4/5.',
    'Hard-stopped trading 20 minutes before high-impact USD releases.',
    'Revenge tags down 40% vs prior 30 sessions.',
  ],
}

export const PRICING = {
  disclosure: 'Development concept pricing. Billing is inactive. No payment processing on this site.',
  annualDiscount: 0.2,
  plans: [
    {
      id: 'free',
      name: 'Free',
      monthly: 0,
      blurb: 'Prove one account without the spreadsheet fog.',
      features: ['1 account', '90 days history', 'Basic analytics', 'Basic public proof'],
      cta: 'Open demo tracker',
      featured: false,
    },
    {
      id: 'pro',
      name: 'Pro',
      monthly: 19,
      blurb: 'Full stack for serious independent traders.',
      features: ['5 accounts', 'Full history', 'Atlas Proof', 'Risk OS', 'Journal Lab'],
      cta: 'Start Pro concept',
      featured: true,
    },
    {
      id: 'desk',
      name: 'Desk',
      monthly: 59,
      blurb: 'Portfolio view for small desks and signal ops.',
      features: ['25 accounts', 'Portfolio rollup', 'Teams', 'Branded proof', 'Advanced export'],
      cta: 'Start Desk concept',
      featured: false,
    },
    {
      id: 'business',
      name: 'Business',
      monthly: 149,
      blurb: 'Operator scale with controls and API concepts.',
      features: ['100 accounts', 'API / export concepts', 'Team permissions', 'Priority onboarding'],
      cta: 'Request early access',
      featured: false,
    },
  ],
} as const

export function fmtMoney(n: number, ccy = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: ccy,
    maximumFractionDigits: 0,
  }).format(n)
}

export function fmtMoneyExact(n: number, ccy = 'USD') {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: ccy,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(n)
}

export function fmtPct(n: number, digits = 1) {
  const sign = n > 0 ? '+' : ''
  return `${sign}${n.toFixed(digits)}%`
}

export function pnlClass(n: number) {
  if (n > 0) return 'lime'
  if (n < 0) return 'danger'
  return ''
}

export function getAccount(id: string) {
  return ACCOUNTS.find((a) => a.id === id) ?? PRIMARY
}
